import React from 'react'
import PropTypes from 'prop-types'
import analyticsService from '../../services/analytics/analytics'
import { ConditionalConsoleExporter } from '../ui/ConditionalConsoleExporter'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Track the error in analytics
    const errorDescription = `${error.name}: ${error.message}`
    analyticsService.trackError(errorDescription, true)

    // Update state with error details for potential debugging display
    this.setState({
      error,
      errorInfo,
    })
  }

  handleReload = () => {
    // Reload the page to recover from the error
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-dark-primary flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            {/* Error Icon */}
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            {/* Error Message */}
            <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-gray-400 mb-8 leading-relaxed">
              We encountered an unexpected error. This has been logged for our team to investigate.
            </p>

            {/* Action */}
            <button
              onClick={this.handleReload}
              className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-dark-primary font-medium"
            >
              Reload Page
            </button>

            {/* Technical Details (only in development) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="text-gray-400 cursor-pointer hover:text-gray-300 mb-2">
                  Technical Details (Development Only)
                </summary>
                <div className="bg-dark-secondary p-4 rounded-lg text-xs text-gray-400 overflow-auto">
                  <div className="mb-2">
                    <strong className="text-gray-300">Error:</strong>
                    <pre className="mt-1 whitespace-pre-wrap">{this.state.error.toString()}</pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong className="text-gray-300">Stack Trace:</strong>
                      <pre className="mt-1 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>

          {/* Console Exporter for debugging (only if enabled or admin) */}
          <ConditionalConsoleExporter />
        </div>
      )
    }

    return this.props.children
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
}

export default ErrorBoundary
